import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronRight, ChevronLeft, User, Users, Target, 
  Calendar, ShoppingCart, Check, ArrowRight, Zap, Award, 
  BookOpen, Clock, Star, Activity, TrendingUp, HelpCircle,
  Dumbbell, Heart, ArrowUp, ArrowDown
} from 'lucide-react';
// Game Icons for anatomical body parts
import { 
  GiChestArmor, GiBackPain, GiShoulderArmor, GiBiceps,
  GiLeg, GiBarefoot, GiMuscularTorso, GiAbdominalArmor
} from 'react-icons/gi';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Body parts data - using Lucide icons for professional look
const BODY_PARTS = [
  { id: 'pectoraux', name: 'Pectoraux', description: 'Poitrine' },
  { id: 'dos', name: 'Dos', description: 'Largeur et épaisseur' },
  { id: 'epaules', name: 'Épaules', description: 'Deltoïdes' },
  { id: 'biceps', name: 'Biceps', description: 'Avant-bras' },
  { id: 'triceps', name: 'Triceps', description: 'Arrière du bras' },
  { id: 'abdominaux', name: 'Abdominaux', description: 'Sangle abdominale' },
  { id: 'jambes', name: 'Jambes', description: 'Quadriceps et ischio-jambiers' },
  { id: 'fessiers', name: 'Fessiers', description: 'Muscles glutéaux' },
  { id: 'mollets', name: 'Mollets', description: 'Gastrocnémien' },
  { id: 'bras', name: 'Bras complet', description: 'Biceps et triceps' },
  { id: 'haut_du_corps', name: 'Haut du corps', description: 'Programme complet' },
  { id: 'bas_du_corps', name: 'Bas du corps', description: 'Programme complet' },
];

const MORPHOLOGIES = [
  { 
    id: 'ectomorphe', 
    name: 'Ectomorphe', 
    description: 'Corps fin, métabolisme rapide, difficultés à prendre du poids'
  },
  { 
    id: 'mesomorphe', 
    name: 'Mésomorphe', 
    description: 'Corps athlétique naturel, facilité à développer du muscle'
  },
  { 
    id: 'endomorphe', 
    name: 'Endomorphe', 
    description: 'Corps plus large, tendance à stocker les graisses facilement'
  },
  { 
    id: 'je_ne_sais_pas', 
    name: 'Je ne sais pas', 
    description: 'Nos programmes s\'adaptent à tous les profils'
  },
];

const FREQUENCIES = [
  { id: '1-2', name: '1 à 2 fois', description: 'Débutant ou emploi du temps chargé' },
  { id: '3', name: '3 fois', description: 'Équilibre parfait pour progresser' },
  { id: '4', name: '4 fois', description: 'Entraînement régulier' },
  { id: '5+', name: '5 fois ou plus', description: 'Pratiquant assidu' },
];

// Progress Bar Component
const ProgressBar = ({ currentStep, totalSteps }) => {
  const progress = ((currentStep) / totalSteps) * 100;
  
  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-dark-950/90 backdrop-blur-md border-b border-white/5">
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-400">Étape {currentStep} sur {totalSteps}</span>
          <span className="text-sm font-medium text-primary-500">{Math.round(progress)}%</span>
        </div>
        <div className="h-2 bg-dark-800 rounded-full overflow-hidden">
          <motion.div 
            className="h-full bg-gradient-to-r from-primary-600 to-primary-500 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        </div>
      </div>
    </div>
  );
};

// Home/Landing Step
const StepHome = ({ onStart }) => {
  return (
    <motion.div 
      className="min-h-screen flex flex-col items-center justify-center px-4 hero-pattern"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="text-center max-w-3xl mx-auto">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <div className="inline-flex items-center gap-2 bg-primary-500/10 border border-primary-500/30 rounded-full px-4 py-2 mb-6">
            <Target className="w-5 h-5 text-primary-500" />
            <span className="text-primary-400 font-medium">Programmes personnalisés</span>
          </div>
        </motion.div>

        <motion.h1 
          className="text-4xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          Construis ton programme de musculation{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-primary-400">
            parfaitement adapté
          </span>{' '}
          à ton corps
        </motion.h1>

        <motion.p 
          className="text-lg md:text-xl text-gray-400 mb-10 max-w-2xl mx-auto"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          En quelques questions, découvre l&apos;e-book qui te fera atteindre tes objectifs plus rapidement.
        </motion.p>

        <motion.button
          data-testid="start-quiz-btn"
          onClick={onStart}
          className="group relative inline-flex items-center gap-3 bg-gradient-to-r from-primary-600 to-primary-500 text-white font-bold text-lg px-10 py-5 rounded-full hover:from-primary-500 hover:to-primary-400 transition-all duration-300 shadow-lg shadow-primary-500/25"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
        >
          Commencer
          <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
        </motion.button>

        {/* Personal expertise section */}
        <motion.div 
          className="mt-16 max-w-xl mx-auto text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          <div className="bg-dark-900/50 border border-dark-700 rounded-2xl p-6">
            <div className="flex items-center justify-center gap-3 mb-3">
              <Dumbbell className="w-6 h-6 text-primary-500" />
              <span className="text-lg font-semibold text-white">11 ans d&apos;expérience</span>
            </div>
            <p className="text-gray-400 leading-relaxed">
              Des programmes testés et perfectionnés par moi-même, avec une vraie expérience terrain en salle de sport. Pas de théorie, que du concret.
            </p>
          </div>
        </motion.div>
      </div>

      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary-600/5 rounded-full blur-3xl" />
      </div>
    </motion.div>
  );
};

// Gender Step
const StepGender = ({ onSelect, selected }) => {
  const options = [
    { id: 'homme', label: 'Homme', icon: User },
    { id: 'femme', label: 'Femme', icon: Users },
  ];

  return (
    <motion.div 
      className="min-h-screen flex flex-col items-center justify-center px-4 pt-24"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
    >
      <div className="text-center max-w-2xl mx-auto w-full">
        <motion.h2 
          className="text-3xl md:text-4xl font-bold mb-4"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
        >
          Tu es :
        </motion.h2>
        <motion.p 
          className="text-gray-400 mb-10"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          Pour personnaliser ton programme
        </motion.p>

        <div className="grid grid-cols-2 gap-4 md:gap-6">
          {options.map((option, index) => (
            <motion.button
              key={option.id}
              data-testid={`gender-${option.id}-btn`}
              onClick={() => onSelect(option.id)}
              className={`relative group p-8 md:p-12 rounded-2xl border-2 transition-all duration-300 card-hover ${
                selected === option.id 
                  ? 'border-primary-500 bg-primary-500/10' 
                  : 'border-dark-700 bg-dark-900/50 hover:border-primary-500/50 hover:bg-dark-800/50'
              }`}
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 + index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <option.icon className={`w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 ${
                selected === option.id ? 'text-primary-500' : 'text-gray-400 group-hover:text-primary-400'
              }`} />
              <span className={`text-xl md:text-2xl font-semibold ${
                selected === option.id ? 'text-primary-500' : 'text-white'
              }`}>
                {option.label}
              </span>
              {selected === option.id && (
                <motion.div 
                  className="absolute top-4 right-4 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                >
                  <Check className="w-4 h-4 text-white" />
                </motion.div>
              )}
            </motion.button>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// Body Part Selection Step - Professional design with Game Icons (anatomical)
const StepBodyPart = ({ onSelect, selected, title, subtitle }) => {
  // Get anatomical icon for each body part - null if no appropriate icon exists
  const getBodyPartIcon = (id) => {
    switch(id) {
      case 'pectoraux': return GiChestArmor;
      case 'dos': return GiBackPain;
      case 'epaules': return GiShoulderArmor;
      case 'biceps': return GiBiceps;
      case 'triceps': return GiBiceps;
      case 'abdominaux': return GiAbdominalArmor;
      case 'jambes': return GiLeg;
      case 'fessiers': return null; // No appropriate icon
      case 'mollets': return GiBarefoot;
      case 'bras': return GiBiceps;
      case 'haut_du_corps': return GiMuscularTorso;
      case 'bas_du_corps': return GiLeg;
      default: return null;
    }
  };

  return (
    <motion.div 
      className="min-h-screen flex flex-col items-center justify-center px-4 pt-24 pb-8"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
    >
      <div className="text-center max-w-4xl mx-auto w-full">
        <motion.h2 
          className="text-3xl md:text-4xl font-bold mb-4"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
        >
          {title}
        </motion.h2>
        <motion.p 
          className="text-gray-400 mb-8"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          {subtitle}
        </motion.p>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {BODY_PARTS.map((part, index) => {
            const IconComponent = getBodyPartIcon(part.id);
            return (
              <motion.button
                key={part.id}
                data-testid={`bodypart-${part.id}-btn`}
                onClick={() => onSelect(part.id)}
                className={`relative group p-5 md:p-6 rounded-xl border-2 transition-all duration-300 card-hover ${
                  selected === part.id 
                    ? 'border-primary-500 bg-primary-500/10' 
                    : 'border-dark-700 bg-dark-900/50 hover:border-primary-500/50 hover:bg-dark-800/50'
                }`}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 + index * 0.03 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {IconComponent && (
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center mx-auto mb-3 ${
                    selected === part.id ? 'bg-primary-500/20' : 'bg-dark-700'
                  }`}>
                    <IconComponent className={`w-5 h-5 md:w-6 md:h-6 ${
                      selected === part.id ? 'text-primary-500' : 'text-gray-400 group-hover:text-primary-400'
                    }`} />
                  </div>
                )}
                <span className={`text-base md:text-lg font-semibold block ${
                  selected === part.id ? 'text-primary-500' : 'text-white'
                }`}>
                  {part.name}
                </span>
                <span className="text-xs text-gray-500 mt-1 block">{part.description}</span>
                {selected === part.id && (
                  <motion.div 
                    className="absolute top-2 right-2 w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                  >
                    <Check className="w-3 h-3 text-white" />
                  </motion.div>
                )}
              </motion.button>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};

// Morphology Step - Professional design
const StepMorphology = ({ onSelect, selected }) => {
  const getIcon = (id) => {
    switch(id) {
      case 'ectomorphe': return Activity;
      case 'mesomorphe': return Dumbbell;
      case 'endomorphe': return Heart;
      default: return HelpCircle;
    }
  };

  return (
    <motion.div 
      className="min-h-screen flex flex-col items-center justify-center px-4 pt-24"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
    >
      <div className="text-center max-w-3xl mx-auto w-full">
        <motion.h2 
          className="text-3xl md:text-4xl font-bold mb-4"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
        >
          Quelle est ta morphologie ?
        </motion.h2>
        <motion.p 
          className="text-gray-400 mb-10"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          Pour adapter les exercices et la nutrition
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {MORPHOLOGIES.map((morpho, index) => {
            const IconComponent = getIcon(morpho.id);
            return (
              <motion.button
                key={morpho.id}
                data-testid={`morphology-${morpho.id}-btn`}
                onClick={() => onSelect(morpho.id)}
                className={`relative group p-6 rounded-xl border-2 text-left transition-all duration-300 card-hover ${
                  selected === morpho.id 
                    ? 'border-primary-500 bg-primary-500/10' 
                    : 'border-dark-700 bg-dark-900/50 hover:border-primary-500/50 hover:bg-dark-800/50'
                }`}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    selected === morpho.id ? 'bg-primary-500/20' : 'bg-dark-700'
                  }`}>
                    <IconComponent className={`w-6 h-6 ${
                      selected === morpho.id ? 'text-primary-500' : 'text-gray-400'
                    }`} />
                  </div>
                  <div>
                    <span className={`text-lg font-semibold block ${
                      selected === morpho.id ? 'text-primary-500' : 'text-white'
                    }`}>
                      {morpho.name}
                    </span>
                    <span className="text-sm text-gray-400 mt-1 block">{morpho.description}</span>
                  </div>
                </div>
                {selected === morpho.id && (
                  <motion.div 
                    className="absolute top-4 right-4 w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                  >
                    <Check className="w-3 h-3 text-white" />
                  </motion.div>
                )}
              </motion.button>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};

// Frequency Step - Professional design
const StepFrequency = ({ onSelect, selected }) => {
  return (
    <motion.div 
      className="min-h-screen flex flex-col items-center justify-center px-4 pt-24"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
    >
      <div className="text-center max-w-2xl mx-auto w-full">
        <motion.h2 
          className="text-3xl md:text-4xl font-bold mb-4"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
        >
          Combien de fois t&apos;entraînes-tu par semaine ?
        </motion.h2>
        <motion.p 
          className="text-gray-400 mb-10"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          Pour calibrer l&apos;intensité de ton programme
        </motion.p>

        <div className="grid grid-cols-2 gap-4">
          {FREQUENCIES.map((freq, index) => (
            <motion.button
              key={freq.id}
              data-testid={`frequency-${freq.id}-btn`}
              onClick={() => onSelect(freq.id)}
              className={`relative group p-6 rounded-xl border-2 transition-all duration-300 card-hover ${
                selected === freq.id 
                  ? 'border-primary-500 bg-primary-500/10' 
                  : 'border-dark-700 bg-dark-900/50 hover:border-primary-500/50 hover:bg-dark-800/50'
              }`}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 + index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Calendar className={`w-8 h-8 mx-auto mb-3 ${
                selected === freq.id ? 'text-primary-500' : 'text-gray-400'
              }`} />
              <span className={`text-lg font-semibold block ${
                selected === freq.id ? 'text-primary-500' : 'text-white'
              }`}>
                {freq.name}
              </span>
              <span className="text-sm text-gray-500 mt-1 block">{freq.description}</span>
              {selected === freq.id && (
                <motion.div 
                  className="absolute top-3 right-3 w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                >
                  <Check className="w-3 h-3 text-white" />
                </motion.div>
              )}
            </motion.button>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// Product Page Step - Professional design
const StepProduct = ({ ebook, onBuy, loading }) => {
  if (!ebook) return null;

  return (
    <motion.div 
      className="min-h-screen flex flex-col items-center justify-center px-4 pt-24 pb-12"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
    >
      <div className="max-w-5xl mx-auto w-full">
        <motion.div 
          className="text-center mb-8"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
        >
          <div className="inline-flex items-center gap-2 bg-primary-500/10 border border-primary-500/30 rounded-full px-4 py-2 mb-4">
            <Award className="w-5 h-5 text-primary-500" />
            <span className="text-primary-400 font-medium">Programme personnalisé pour toi</span>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* E-book Image */}
          <motion.div
            initial={{ x: -30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-primary-500/20">
              <img 
                src={ebook.image} 
                alt={ebook.title}
                className="w-full h-auto object-cover aspect-[4/3]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-dark-950/80 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <div className="glass rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-primary-500" />
                      <span className="text-sm font-medium">E-book PDF</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-primary-500" />
                      <span className="text-sm font-medium">Accès instantané</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Decorative elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary-500/20 rounded-full blur-2xl" />
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-primary-600/20 rounded-full blur-2xl" />
          </motion.div>

          {/* E-book Details */}
          <motion.div
            initial={{ x: 30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <h1 className="text-3xl md:text-4xl font-bold mb-4">{ebook.title}</h1>
            <p className="text-gray-400 text-lg mb-6">{ebook.description}</p>

            {/* Features */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-gray-300">Ce que tu vas obtenir :</h3>
              <ul className="space-y-3">
                {ebook.features.map((feature, index) => (
                  <motion.li 
                    key={index}
                    className="flex items-start gap-3"
                    initial={{ x: 20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                  >
                    <div className="w-6 h-6 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-primary-500" />
                    </div>
                    <span className="text-gray-300">{feature}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Price and CTA */}
            <div className="bg-dark-800/50 rounded-2xl p-6 border border-dark-700">
              <div className="flex items-center gap-3 mb-6">
                <span className="text-4xl md:text-5xl font-black text-white">{ebook.price.toFixed(0)}€</span>
                <span className="text-gray-400 text-lg">TTC</span>
              </div>

              <motion.button
                data-testid="buy-ebook-btn"
                onClick={onBuy}
                disabled={loading}
                className="w-full group relative inline-flex items-center justify-center gap-3 bg-gradient-to-r from-primary-600 to-primary-500 text-white font-bold text-lg px-8 py-5 rounded-xl hover:from-primary-500 hover:to-primary-400 transition-all duration-300 shadow-lg shadow-primary-500/25 disabled:opacity-70 disabled:cursor-not-allowed"
                whileHover={{ scale: loading ? 1 : 1.02 }}
                whileTap={{ scale: loading ? 1 : 0.98 }}
              >
                {loading ? (
                  <>
                    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Redirection vers le paiement...
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-6 h-6" />
                    Acheter l&apos;e-book
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </motion.button>

              <div className="flex items-center justify-center gap-6 mt-4 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Paiement sécurisé</span>
                </div>
                <div className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Accès immédiat</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

// Success Page
const SuccessPage = () => {
  return (
    <motion.div 
      className="min-h-screen flex flex-col items-center justify-center px-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <motion.div 
        className="text-center max-w-lg mx-auto"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="w-12 h-12 text-green-500" />
        </div>
        <h1 className="text-3xl md:text-4xl font-bold mb-4">Merci pour ton achat</h1>
        <p className="text-gray-400 mb-8">
          Ton e-book a été envoyé à ton adresse email. Vérifie également tes spams si tu ne le reçois pas dans les prochaines minutes.
        </p>
        <button
          data-testid="back-home-btn"
          onClick={() => window.location.href = '/'}
          className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
        >
          Retour à l&apos;accueil
        </button>
      </motion.div>
    </motion.div>
  );
};

// Cancel Page
const CancelPage = () => {
  return (
    <motion.div 
      className="min-h-screen flex flex-col items-center justify-center px-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <motion.div 
        className="text-center max-w-lg mx-auto"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <div className="w-24 h-24 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <ChevronLeft className="w-12 h-12 text-yellow-500" />
        </div>
        <h1 className="text-3xl md:text-4xl font-bold mb-4">Paiement annulé</h1>
        <p className="text-gray-400 mb-8">
          Tu as annulé le paiement. Si tu as des questions, n&apos;hésite pas à nous contacter.
        </p>
        <button
          data-testid="back-quiz-btn"
          onClick={() => window.location.href = '/'}
          className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
        >
          Recommencer le quiz
        </button>
      </motion.div>
    </motion.div>
  );
};

// Navigation Buttons
const NavigationButtons = ({ onBack, onNext, showBack, showNext, nextDisabled, nextLabel = "Continuer" }) => {
  return (
    <motion.div 
      className="fixed bottom-0 left-0 right-0 bg-dark-950/90 backdrop-blur-md border-t border-white/5 p-4"
      initial={{ y: 100 }}
      animate={{ y: 0 }}
    >
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
        {showBack ? (
          <button
            data-testid="back-btn"
            onClick={onBack}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors px-4 py-3"
          >
            <ChevronLeft className="w-5 h-5" />
            Retour
          </button>
        ) : (
          <div />
        )}
        
        {showNext && (
          <motion.button
            data-testid="next-btn"
            onClick={onNext}
            disabled={nextDisabled}
            className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:bg-dark-700 disabled:text-gray-500 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
            whileHover={{ scale: nextDisabled ? 1 : 1.02 }}
            whileTap={{ scale: nextDisabled ? 1 : 0.98 }}
          >
            {nextLabel}
            <ChevronRight className="w-5 h-5" />
          </motion.button>
        )}
      </div>
    </motion.div>
  );
};

// Main App Component
function App() {
  const [step, setStep] = useState(0);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [loading, setLoading] = useState(false);
  const [ebook, setEbook] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    sexe: '',
    partieCorps: '',
    morphologie: '',
    frequence: '',
    objectifFinal: ''
  });

  // Check URL for success/cancel pages
  const urlParams = new URLSearchParams(window.location.search);
  const sessionIdFromUrl = urlParams.get('session_id');
  const isSuccessPage = window.location.pathname === '/success' || window.location.pathname.includes('/success');
  const isCancelPage = window.location.pathname === '/cancel' || window.location.pathname.includes('/cancel');

  // Poll payment status on success page
  useEffect(() => {
    if (isSuccessPage && sessionIdFromUrl) {
      const pollPaymentStatus = async (attempts = 0) => {
        const maxAttempts = 5;
        if (attempts >= maxAttempts) return;

        try {
          const response = await fetch(`${BACKEND_URL}/api/checkout/status/${sessionIdFromUrl}`);
          if (response.ok) {
            const data = await response.json();
            if (data.payment_status === 'paid') {
              console.log('Payment confirmed!');
              return;
            }
          }
          // Continue polling
          setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
        } catch (error) {
          console.error('Error polling payment status:', error);
        }
      };
      
      pollPaymentStatus();
    }
  }, [isSuccessPage, sessionIdFromUrl]);

  // Fetch ebook details when reaching product step
  useEffect(() => {
    if (step === 6 && formData.objectifFinal) {
      const fetchEbook = async () => {
        try {
          const response = await fetch(`${BACKEND_URL}/api/ebooks/${formData.objectifFinal}`);
          if (response.ok) {
            const data = await response.json();
            setEbook(data);
          }
        } catch (error) {
          console.error('Error fetching ebook:', error);
        }
      };
      fetchEbook();
    }
  }, [step, formData.objectifFinal]);

  // Handle page routing
  if (isSuccessPage) {
    return <SuccessPage />;
  }

  if (isCancelPage) {
    return <CancelPage />;
  }

  const totalSteps = 6;

  const handleNext = () => {
    if (step < 6) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleSelectGender = (gender) => {
    setFormData({ ...formData, sexe: gender });
    setTimeout(() => handleNext(), 300);
  };

  const handleSelectBodyPart = (part) => {
    setFormData({ ...formData, partieCorps: part });
    setTimeout(() => handleNext(), 300);
  };

  const handleSelectMorphology = (morpho) => {
    setFormData({ ...formData, morphologie: morpho });
    setTimeout(() => handleNext(), 300);
  };

  const handleSelectFrequency = (freq) => {
    setFormData({ ...formData, frequence: freq });
    setTimeout(() => handleNext(), 300);
  };

  const handleSelectObjectif = (objectif) => {
    setFormData({ ...formData, objectifFinal: objectif });
    setTimeout(() => handleNext(), 300);
  };

  const handleBuy = async () => {
    setLoading(true);
    
    try {
      // Save user profile
      await fetch(`${BACKEND_URL}/api/user-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          sexe: formData.sexe,
          partie_corps: formData.partieCorps,
          morphologie: formData.morphologie,
          frequence: formData.frequence,
          objectif_final: formData.objectifFinal
        })
      });

      // Create checkout session
      const response = await fetch(`${BACKEND_URL}/api/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ebook_id: formData.objectifFinal,
          origin_url: window.location.origin,
          user_session_id: sessionId
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Une erreur est survenue. Veuillez réessayer.');
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return formData.sexe !== '';
      case 2: return formData.partieCorps !== '';
      case 3: return formData.morphologie !== '';
      case 4: return formData.frequence !== '';
      case 5: return formData.objectifFinal !== '';
      default: return true;
    }
  };

  return (
    <div className="App min-h-screen bg-dark-950">
      {/* Progress bar - only show during quiz */}
      {step > 0 && step < 6 && (
        <ProgressBar currentStep={step} totalSteps={totalSteps - 1} />
      )}

      <AnimatePresence mode="wait">
        {step === 0 && (
          <StepHome key="home" onStart={() => setStep(1)} />
        )}
        
        {step === 1 && (
          <StepGender 
            key="gender" 
            onSelect={handleSelectGender} 
            selected={formData.sexe} 
          />
        )}
        
        {step === 2 && (
          <StepBodyPart 
            key="bodypart" 
            onSelect={handleSelectBodyPart} 
            selected={formData.partieCorps}
            title="Quelle partie du corps souhaites-tu développer en priorité ?"
            subtitle="Sélectionne la zone que tu veux cibler"
          />
        )}
        
        {step === 3 && (
          <StepMorphology 
            key="morphology" 
            onSelect={handleSelectMorphology} 
            selected={formData.morphologie} 
          />
        )}
        
        {step === 4 && (
          <StepFrequency 
            key="frequency" 
            onSelect={handleSelectFrequency} 
            selected={formData.frequence} 
          />
        )}
        
        {step === 5 && (
          <StepBodyPart 
            key="objectif" 
            onSelect={handleSelectObjectif} 
            selected={formData.objectifFinal}
            title="Quel muscle souhaites-tu vraiment transformer ?"
            subtitle="Ce sera l'e-book parfait pour toi"
          />
        )}
        
        {step === 6 && (
          <StepProduct 
            key="product" 
            ebook={ebook} 
            onBuy={handleBuy}
            loading={loading}
          />
        )}
      </AnimatePresence>

      {/* Navigation buttons - only show during quiz (not on home or product page) */}
      {step > 0 && step < 6 && (
        <NavigationButtons 
          onBack={handleBack}
          onNext={handleNext}
          showBack={step > 1}
          showNext={false}
          nextDisabled={!canProceed()}
        />
      )}
      
      {/* Back button on product page */}
      {step === 6 && (
        <motion.div 
          className="fixed bottom-0 left-0 right-0 bg-dark-950/90 backdrop-blur-md border-t border-white/5 p-4"
          initial={{ y: 100 }}
          animate={{ y: 0 }}
        >
          <div className="max-w-4xl mx-auto">
            <button
              data-testid="back-btn"
              onClick={handleBack}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors px-4 py-3"
            >
              <ChevronLeft className="w-5 h-5" />
              Modifier mes réponses
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default App;
